
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, Download, ArrowLeft, Loader2 } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';

export default function PurchaseSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const navigate = useNavigate();
  const [verified, setVerified] = useState(false);

  // Check if the purchase is verified in our database
  const { data: purchaseData, isLoading } = useQuery({
    queryKey: ['purchase-verification', sessionId],
    queryFn: async () => {
      if (!sessionId) return null;
      
      const { data, error } = await supabase
        .from('purchased_itineraries')
        .select('*, premium_itineraries(*)')
        .eq('stripe_session_id', sessionId)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!sessionId,
    staleTime: Infinity,
    retry: 3
  });

  // Update the verified state when purchase data is available
  useEffect(() => {
    if (purchaseData) {
      setVerified(true);
    }
  }, [purchaseData]);

  // Handle download
  const handleDownload = () => {
    // This will be implemented in Stage 5
    toast.info("PDF download functionality will be implemented in Stage 5");
  };

  // If no session ID is provided, show an error
  if (!sessionId) {
    return (
      <div className="container py-16">
        <Card className="max-w-lg mx-auto">
          <CardHeader>
            <CardTitle className="text-center text-destructive">Invalid Purchase Session</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col items-center space-y-6">
            <p className="text-center text-muted-foreground">
              We couldn't verify your purchase. Please contact support if you believe this is an error.
            </p>
            <Button variant="outline" onClick={() => navigate('/premium-itineraries')}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Return to Premium Itineraries
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container py-16">
      <Card className="max-w-lg mx-auto">
        <CardHeader>
          <div className="flex flex-col items-center">
            <CheckCircle className="h-16 w-16 text-green-500 mb-4" />
            <CardTitle className="text-center text-2xl">Thank You for Your Purchase!</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="flex flex-col items-center space-y-6">
          {isLoading ? (
            <div className="flex flex-col items-center py-6">
              <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
              <p className="text-muted-foreground">Verifying your purchase...</p>
            </div>
          ) : verified ? (
            <>
              <div className="text-center space-y-2">
                <h3 className="text-xl font-medium">
                  {purchaseData?.premium_itineraries?.title || 'Premium Itinerary'}
                </h3>
                <p className="text-muted-foreground">
                  Your premium itinerary purchase has been completed successfully.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 w-full">
                <Button 
                  className="flex-1" 
                  onClick={handleDownload}
                >
                  <Download className="mr-2 h-4 w-4" />
                  Download Itinerary
                </Button>
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => navigate(`/premium-itineraries/${purchaseData?.premium_itinerary_id}`)}
                >
                  View Itinerary
                </Button>
              </div>
            </>
          ) : (
            <div className="text-center space-y-4">
              <p className="text-muted-foreground">
                We're processing your payment. This may take a few moments.
              </p>
              <Button variant="outline" onClick={() => navigate('/premium-itineraries')}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Return to Premium Itineraries
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
